import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { FColor, FNumber, FSelect, FImage, FCheck, fieldLabel, fieldInput, focusRing, blurRing, resolveSrc } from './builderFields';

/* =====================================================================
 * Unified widget style system — used by EVERY widget in:
 * pages (برگه‌ها), invoice template (فاکتور), header and footer (هدر/فوتر).
 *
 * Every widget carries TWO separated settings groups:
 *   1. settings.style   → «تنظیمات استایل»   (how it looks)
 *   2. everything else  → «تنظیمات نمایش محتوا» (what it shows)
 *
 * Old widgets that only have legacy keys (frame.*, padding, radius...)
 * keep working: renderers fall back to the legacy path when style is empty.
 * ===================================================================== */

export const SHADOW_OPTS = [
  { value: '', label: 'بدون سایه' },
  { value: 'sm', label: 'کم' },
  { value: 'md', label: 'متوسط' },
  { value: 'lg', label: 'زیاد' },
  { value: 'xl', label: 'خیلی زیاد' },
];

export const TEXT_SHADOW_CSS = {
  sm: '0 1px 2px rgba(0,0,0,0.35)',
  md: '0 2px 8px rgba(0,0,0,0.40)',
  lg: '0 4px 18px rgba(0,0,0,0.45)',
};
const TEXT_SHADOW_OPTS = [
  { value: '', label: 'بدون سایه متن' },
  { value: 'sm', label: 'کم' },
  { value: 'md', label: 'متوسط' },
  { value: 'lg', label: 'زیاد' },
];

export const SHADOW_CSS = {
  sm: '0 1px 2px rgba(0,0,0,0.06)',
  md: '0 4px 12px rgba(0,0,0,0.08)',
  lg: '0 8px 30px rgba(28,25,23,0.10)',
  xl: '0 16px 48px rgba(28,25,23,0.14)',
};

/* ---------- legacy frame object → React style (single source of truth) ---------- */
export function frameStyle(f) {
  if (!f) return undefined;
  const st = {};
  if (f.bg && f.bg !== 'transparent') st.background = f.bg;
  if (f.padding) st.padding = `${f.padding}px`;
  const tl = f.radiusTL ?? f.radius ?? 0, tr = f.radiusTR ?? f.radius ?? 0;
  const br = f.radiusBR ?? f.radius ?? 0, bl = f.radiusBL ?? f.radius ?? 0;
  if (tl || tr || br || bl) { st.borderRadius = `${tl}px ${tr}px ${br}px ${bl}px`; st.overflow = 'hidden'; }
  if (f.shadow && SHADOW_CSS[f.shadow]) st.boxShadow = SHADOW_CSS[f.shadow];
  const mT = f.marginT ?? 0, mR = f.marginR ?? 0, mB = f.marginB ?? 0, mL = f.marginL ?? 0;
  if (mT || mR || mB || mL) st.margin = `${mT}px ${mR}px ${mB}px ${mL}px`;
  return Object.keys(st).length ? st : undefined;
}

export const FONT_FAMILIES = [
  { value: '', label: 'پیش‌فرض سایت' },
  { value: 'IRANSans', label: 'ایران‌سنس' },
  { value: 'Rokh', label: 'رخ (Rokh)' },
  { value: 'Vazirmatn', label: 'وزیرمتن' },
  { value: 'Tahoma', label: 'Tahoma' },
  { value: 'Arial', label: 'Arial' },
];

/** Register uploaded custom fonts so every widget font picker offers them. */
export function registerCustomFonts(families = []) {
  let added = false;
  families.forEach(f => {
    if (!f || !f.value) return;
    if (!FONT_FAMILIES.some(x => x.value === f.value)) {
      FONT_FAMILIES.push({ value: f.value, label: f.label || f.value });
      added = true;
    }
  });
  if (added && typeof window !== 'undefined') {
    window.dispatchEvent(new Event('site-fonts-ready'));
  }
  return added;
}

const num = v => (v === '' || v === undefined || v === null || isNaN(Number(v)) ? undefined : Number(v));

/* ---------- normalize: always return full shape (never crash on partial data) ---------- */
export function normalizeStyle(v = {}) {
  const o = v || {};
  const sides = (s = {}) => ({ t: num(s.t) ?? 0, r: num(s.r) ?? 0, b: num(s.b) ?? 0, l: num(s.l) ?? 0 });
  const corners = (c = {}) => ({ tl: num(c.tl) ?? 0, tr: num(c.tr) ?? 0, br: num(c.br) ?? 0, bl: num(c.bl) ?? 0 });
  return {
    margin: sides(o.margin),
    padding: sides(o.padding),
    border: { style: o.border?.style || 'solid', size: num(o.border?.size) ?? 0, color: o.border?.color || 'var(--border)' },
    radius: corners(o.radius),
    width: { value: o.width?.value ?? '', unit: o.width?.unit || '%' },
    height: { value: o.height?.value ?? '', unit: o.height?.unit || 'px' },
    shadow: o.shadow || '',
    bg: o.bg || '',
    bgImage: o.bgImage || '',
    bgSize: o.bgSize || 'cover',
    bgPosition: o.bgPosition || 'center',
    font: {
      family: o.font?.family || '', size: num(o.font?.size), sizeMobile: num(o.font?.sizeMobile),
      weight: o.font?.weight || '', color: o.font?.color || '', align: o.font?.align || '', lineHeight: o.font?.lineHeight || '',
    },
    blur: num(o.blur) ?? 0,
    opacity: num(o.opacity) ?? 100,
    overflow: !!o.overflow,
    gradFrom: o.gradFrom || '',
    gradTo: o.gradTo || '',
    gradAngle: num(o.gradAngle) ?? 135,
    textShadow: o.textShadow || '',
    rotate: num(o.rotate) ?? 0,
    letterSpacing: num(o.letterSpacing) ?? 0,
    parts: normalizeParts(o.parts),
  };
}

/* ---------- ریزه‌کاری‌های داخلی ویجت: تیتر / توضیح / دکمه / آیکون / بج / کارت ---------- */
function normalizeParts(pp = {}) {
  const o = pp || {};
  return {
    title: { size: num(o.title?.size), weight: o.title?.weight || '', color: o.title?.color || '', align: o.title?.align || '' },
    desc: { size: num(o.desc?.size), color: o.desc?.color || '', lineHeight: num(o.desc?.lineHeight) },
    button: {
      style: o.button?.style || '', radius: num(o.button?.radius), bg: o.button?.bg || '', color: o.button?.color || '',
      weight: o.button?.weight || '', size: num(o.button?.size), padY: num(o.button?.padY), padX: num(o.button?.padX),
    },
    icon: { size: num(o.icon?.size), radius: num(o.icon?.radius), bg: o.icon?.bg || '', color: o.icon?.color || '' },
    chip: { radius: num(o.chip?.radius), bg: o.chip?.bg || '', color: o.chip?.color || '' },
    card: { gap: num(o.card?.gap) },
  };
}

/** استایل یک جزء داخلی ویجت (تیتر/توضیح/دکمه/آیکون/بج/کارت) — فقط موارد پرشده برمی‌گردند. */
export function partStyle(styleValue, part) {
  const s = normalizeStyle(styleValue);
  const p = s.parts?.[part];
  if (!p) return {};
  const st = {};
  if (part === 'title') {
    if (p.size) st.fontSize = `${p.size}px`;
    if (p.weight) st.fontWeight = Number(p.weight);
    if (p.color) st.color = p.color;
    if (p.align) st.textAlign = p.align;
  } else if (part === 'desc') {
    if (p.size) st.fontSize = `${p.size}px`;
    if (p.color) st.color = p.color;
    if (p.lineHeight) st.lineHeight = Number(p.lineHeight);
  } else if (part === 'button') {
    if (p.radius !== undefined) st.borderRadius = `${p.radius}px`;
    if (p.size) st.fontSize = `${p.size}px`;
    if (p.weight) st.fontWeight = Number(p.weight);
    if (p.padY !== undefined || p.padX !== undefined) st.padding = `${p.padY ?? 9}px ${p.padX ?? 20}px`;
    if (p.style === 'outline') {
      st.background = 'transparent';
      st.color = p.color || 'var(--primary)';
      st.border = `1.5px solid ${p.color || 'var(--primary)'}`;
    } else if (p.style === 'soft') {
      st.background = p.bg || 'color-mix(in srgb, var(--primary) 12%, transparent)';
      st.color = p.color || 'var(--primary)';
    } else {
      if (p.bg) st.background = p.bg;
      if (p.color) st.color = p.color;
    }
  } else if (part === 'icon') {
    if (p.size) { st.width = `${p.size}px`; st.height = `${p.size}px`; }
    if (p.radius !== undefined) st.borderRadius = `${p.radius}px`;
    if (p.bg) st.background = p.bg;
    if (p.color) st.color = p.color;
  } else if (part === 'chip') {
    if (p.radius !== undefined) st.borderRadius = `${p.radius}px`;
    if (p.bg) st.background = p.bg;
    if (p.color) st.color = p.color;
  } else if (part === 'card') {
    if (p.gap !== undefined) st.gap = `${p.gap}px`;
  }
  return st;
}

export function isStyleEmpty(v) {
  if (!v) return true;
  const s = normalizeStyle(v);
  return (
    !s.margin.t && !s.margin.r && !s.margin.b && !s.margin.l &&
    !s.padding.t && !s.padding.r && !s.padding.b && !s.padding.l &&
    !s.border.size && !s.radius.tl && !s.radius.tr && !s.radius.br && !s.radius.bl &&
    !s.width.value && !s.height.value && !s.shadow && !s.bg && !s.bgImage &&
    !s.font.family && s.font.size === undefined && s.font.sizeMobile === undefined &&
    !s.font.weight && !s.font.color && !s.font.align && !s.font.lineHeight &&
    !s.blur && (s.opacity === 100 || s.opacity === undefined) && !s.overflow &&
    !s.gradFrom && !s.gradTo && !s.textShadow && !s.rotate && !s.letterSpacing &&
    !Object.values(s.parts || {}).some(pp => pp && Object.values(pp).some(x => x !== '' && x !== undefined && x !== null))
  );
}

/* ---------- React style object for live renderers (site, header, footer) ---------- */
export function widgetStyleObj(v) {
  // No style object at all → legacy rendering untouched.
  if (v === undefined || v === null) return undefined;
  if (isStyleEmpty(v)) return undefined;
  const s = normalizeStyle(v);
  const st = {};
  st.margin = `${s.margin.t}px ${s.margin.r}px ${s.margin.b}px ${s.margin.l}px`;
  if (s.padding.t || s.padding.r || s.padding.b || s.padding.l) st.padding = `${s.padding.t}px ${s.padding.r}px ${s.padding.b}px ${s.padding.l}px`;
  if (s.border.size > 0 && s.border.style !== 'none') st.border = `${s.border.size}px ${s.border.style} ${s.border.color}`;
  if (s.radius.tl || s.radius.tr || s.radius.br || s.radius.bl) st.borderRadius = `${s.radius.tl}px ${s.radius.tr}px ${s.radius.br}px ${s.radius.bl}px`;
  if (s.width.value !== '' && s.width.unit !== 'auto') st.width = `${s.width.value}${s.width.unit}`;
  if (s.height.value !== '' && s.height.unit !== 'auto') st.height = `${s.height.value}${s.height.unit}`;
  // Explicit shadow choice ALWAYS wins over a legacy frame shadow:
  // '' (بدون سایه) actively removes it, otherwise the preset applies.
  st.boxShadow = (s.shadow && SHADOW_CSS[s.shadow]) ? SHADOW_CSS[s.shadow] : 'none';
  if (s.bg) st.backgroundColor = s.bg;
  if (s.gradFrom || s.gradTo) {
    const from = s.gradFrom || 'transparent', to = s.gradTo || 'transparent';
    st.backgroundImage = `linear-gradient(${s.gradAngle ?? 135}deg, ${from}, ${to})`;
  }
  if (s.bgImage) {
    st.backgroundImage = `url("${resolveSrc(s.bgImage)}")`;
    st.backgroundSize = s.bgSize;
    st.backgroundPosition = s.bgPosition;
    st.backgroundRepeat = 'no-repeat';
  }
  if (s.font.family) st.fontFamily = s.font.family;
  const isMobileView = typeof window !== 'undefined' && window.innerWidth < 768;
  const fontSize = (isMobileView && s.font.sizeMobile !== undefined) ? s.font.sizeMobile : s.font.size;
  if (fontSize !== undefined) st.fontSize = `${fontSize}px`;
  if (s.font.weight) st.fontWeight = Number(s.font.weight);
  if (s.font.color) st.color = s.font.color;
  if (s.font.align) st.textAlign = s.font.align;
  if (s.font.lineHeight) st.lineHeight = Number(s.font.lineHeight);
  if (s.textShadow && TEXT_SHADOW_CSS[s.textShadow]) st.textShadow = TEXT_SHADOW_CSS[s.textShadow];
  if (s.letterSpacing) st.letterSpacing = `${s.letterSpacing}px`;
  if (s.rotate) st.transform = `rotate(${s.rotate}deg)`;
  if (s.blur > 0) st.backdropFilter = `blur(${s.blur}px)`;
  if (s.opacity !== undefined && s.opacity < 100) st.opacity = s.opacity / 100;
  if (s.overflow || s.radius.tl || s.radius.tr || s.radius.br || s.radius.bl) st.overflow = 'hidden';
  return st;
}

/* ---------- CSS string for HTML-string renderers (invoice PDF) ---------- */
export function widgetStyleCss(v) {
  const o = widgetStyleObj(v);
  if (!o) return '';
  const kebab = k => k.replace(/[A-Z]/g, m => '-' + m.toLowerCase());
  return Object.entries(o).map(([k, val]) => `${kebab(k)}:${val}`).join(';');
}

/* ---------- collapsible admin section shell ---------- */
function Section({ title, hint, badge, children, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="flex w-full cursor-pointer items-center justify-between border-0 p-3 px-4 font-[inherit] text-[14px] font-bold"
        style={open ? { background: 'hsl(var(--foreground))', color: 'hsl(var(--background))' } : { background: 'hsl(var(--muted))', color: 'hsl(var(--foreground))' }}>
        <span>{title}</span>
        <span className="inline-flex items-center gap-2">
          {badge && <span className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold" style={open ? { background: 'rgba(255,255,255,0.2)' } : { background: 'hsl(var(--accent))', color: 'hsl(var(--accent-foreground))' }}>{badge}</span>}
          <span className="inline-flex transition-transform" style={{ transform: open ? 'rotate(180deg)' : 'none' }}><ChevronDown size={15} /></span>
        </span>
      </button>
      {open && <div className="p-3.5"><div className="mb-2.5 text-xs text-muted-foreground">{hint}</div>{children}</div>}
    </div>
  );
}

export function WidgetContentSection({ children }) {
  return (
    <Section title="تنظیمات نمایش محتوا" hint="مشخص کنید این ویجت چه محتوایی نمایش بدهد (متن، تصویر، لینک، آیتم‌ها...)" defaultOpen>
      {children}
    </Section>
  );
}

/* ---------- 4-direction inputs ---------- */
function SidesInput({ title, value, onChange }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val === '' ? 0 : Number(val) });
  const cell = (k, label) => (
    <div><label style={fieldLabel}>{label}</label>
      <input type="number" value={v[k] ?? 0} onChange={e => set(k, e.target.value)} min={0} max={200}
        style={fieldInput} onFocus={focusRing} onBlur={blurRing} /></div>
  );
  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <div style={fieldLabel}>{title} (px)</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {cell('t', 'بالا')}{cell('r', 'راست')}{cell('b', 'پایین')}{cell('l', 'چپ')}
      </div>
    </div>
  );
}

/* گردی چهار گوشه — همان مدل کادر فاصله‌ها (۴ باکس کنار هم) */
function CornersInput({ title, value, onChange }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val === '' ? 0 : Number(val) });
  const cell = (k, label) => (
    <div><label style={fieldLabel}>{label}</label>
      <input type="number" value={v[k] ?? 0} onChange={e => set(k, e.target.value)} min={0} max={120}
        style={fieldInput} onFocus={focusRing} onBlur={blurRing} /></div>
  );
  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <div style={fieldLabel}>{title} (px)</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {cell('tr', 'بالا راست')}{cell('tl', 'بالا چپ')}{cell('br', 'پایین راست')}{cell('bl', 'پایین چپ')}
      </div>
    </div>
  );
}

/* ---------- THE complete style panel ---------- */
export function WidgetStyleSection({ value, onChange, defaultOpen }) {
  const s = normalizeStyle(value);
  const set = patch => onChange({ ...s, ...patch });
  const setSide = (key, obj) => onChange({ ...s, [key]: obj });
  const setPart = (part, patch) => onChange({ ...s, parts: { ...s.parts, [part]: { ...(s.parts[part] || {}), ...patch } } });
  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 };
  const groupTitle = t => <div className="col-span-full mb-1 mt-1 border-b border-dashed border-border pb-1.5 text-xs font-bold">{t}</div>;
  const filledCount = isStyleEmpty(value) ? null : 'فعال';

  return (
    <Section title="تنظیمات استایل" hint="ظاهر ویجت: فاصله‌ها، حاشیه، ابعاد، سایه، پس‌زمینه، فونت و افکت‌ها" badge={filledCount} defaultOpen={defaultOpen}>
      <div style={grid}>
        {groupTitle('فاصله بیرونی (Margin)')}
        <SidesInput title="فاصله بیرونی از چهار طرف" value={s.margin} onChange={v => setSide('margin', v)} />
        {groupTitle('فاصله داخلی (Padding)')}
        <SidesInput title="فاصله داخلی از چهار طرف" value={s.padding} onChange={v => setSide('padding', v)} />
        {groupTitle('حاشیه (Border)')}
        <FSelect label="مدل حاشیه" value={s.border.style} onChange={v => set({ border: { ...s.border, style: v } })}
          options={[{ value: 'solid', label: 'توپر' }, { value: 'dashed', label: 'خط‌چین' }, { value: 'dotted', label: 'نقطه‌چین' }, { value: 'double', label: 'دوبل' }, { value: 'none', label: 'بدون حاشیه' }]} />
        <FNumber label="ضخامت حاشیه (px)" value={s.border.size} onChange={v => set({ border: { ...s.border, size: v } })} min={0} max={20} />
        <FColor label="رنگ حاشیه" value={s.border.color} onChange={v => set({ border: { ...s.border, color: v } })} />
        {groupTitle('گردی گوشه‌ها (Radius)')}
        <CornersInput title="گردی هر گوشه" value={s.radius} onChange={v => set({ radius: v })} />
        {groupTitle('ابعاد (Width / Height)')}
        <FNumber label="عرض" value={s.width.value} onChange={v => set({ width: { ...s.width, value: v } })} min={0} max={2000} />
        <FSelect label="واحد عرض" value={s.width.unit} onChange={v => set({ width: { ...s.width, unit: v } })}
          options={[{ value: '%', label: '٪ از والد' }, { value: 'px', label: 'پیکسل' }, { value: 'auto', label: 'خودکار' }]} />
        <FNumber label="ارتفاع" value={s.height.value} onChange={v => set({ height: { ...s.height, value: v } })} min={0} max={2000} />
        <FSelect label="واحد ارتفاع" value={s.height.unit} onChange={v => set({ height: { ...s.height, unit: v } })}
          options={[{ value: 'px', label: 'پیکسل' }, { value: '%', label: '٪ از والد' }, { value: 'auto', label: 'خودکار' }]} />
        {groupTitle('سایه (Shadow)')}
        <FSelect label="سایه" value={s.shadow} onChange={v => set({ shadow: v })} options={SHADOW_OPTS} />
        {groupTitle('پس‌زمینه (Background)')}
        <FColor label="رنگ پس‌زمینه" value={s.bg} onChange={v => set({ bg: v })} />
        <div style={{ gridColumn: '1 / -1' }}>
          <FImage label="عکس پس‌زمینه (آپلود از سیستم)" value={s.bgImage} onChange={v => set({ bgImage: v })} />
        </div>
        <FSelect label="اندازه عکس پس‌زمینه" value={s.bgSize} onChange={v => set({ bgSize: v })}
          options={[{ value: 'cover', label: 'پوشش کامل (cover)' }, { value: 'contain', label: 'نمایش کامل (contain)' }, { value: 'auto', label: 'اندازه واقعی' }, { value: '100% 100%', label: 'کشیده تمام‌قد' }]} />
        <FSelect label="موقعیت عکس پس‌زمینه" value={s.bgPosition} onChange={v => set({ bgPosition: v })}
          options={[{ value: 'center', label: 'وسط' }, { value: 'top', label: 'بالا' }, { value: 'bottom', label: 'پایین' }, { value: 'right', label: 'راست' }, { value: 'left', label: 'چپ' }]} />
        {groupTitle('فونت (Font)')}
        <FSelect label="خانواده فونت" value={s.font.family} onChange={v => set({ font: { ...s.font, family: v } })} options={FONT_FAMILIES} />
        <FNumber label="اندازه فونت (px)" value={s.font.size} onChange={v => set({ font: { ...s.font, size: v } })} min={8} max={96} />
        <FNumber label="فونت موبایل (px)" value={s.font.sizeMobile} onChange={v => set({ font: { ...s.font, sizeMobile: v } })} min={8} max={64} />
        <FSelect label="ضخامت فونت" value={s.font.weight} onChange={v => set({ font: { ...s.font, weight: v } })}
          options={[{ value: '', label: 'معمولی' }, { value: '400', label: 'نرمال' }, { value: '600', label: 'نیم‌ضخیم' }, { value: '700', label: 'ضخیم' }, { value: '800', label: 'خیلی ضخیم' }]} />
        <FColor label="رنگ فونت" value={s.font.color} onChange={v => set({ font: { ...s.font, color: v } })} />
        <FSelect label="تراز متن" value={s.font.align} onChange={v => set({ font: { ...s.font, align: v } })}
          options={[{ value: '', label: 'پیش‌فرض' }, { value: 'right', label: 'راست' }, { value: 'center', label: 'وسط' }, { value: 'left', label: 'چپ' }]} />
        <FNumber label="فاصله خطوط" value={s.font.lineHeight} onChange={v => set({ font: { ...s.font, lineHeight: v } })} min={1} max={4} />
        {groupTitle('افکت‌ها')}
        <FNumber label="بلور پس‌زمینه (px)" value={s.blur} onChange={v => set({ blur: v })} min={0} max={20} />
        <FNumber label="شفافیت (٪)" value={s.opacity} onChange={v => set({ opacity: v })} min={10} max={100} />
        <FCheck label="برش لبه‌ها (مخفی‌کردن بیرون‌زدگی)" checked={s.overflow} onChange={v => set({ overflow: v })} />
        {groupTitle('گرادیان پس‌زمینه')}
        <FColor label="رنگ شروع" value={s.gradFrom} onChange={v => set({ gradFrom: v })} />
        <FColor label="رنگ پایان" value={s.gradTo} onChange={v => set({ gradTo: v })} />
        <FNumber label="زاویه (درجه)" value={s.gradAngle ?? 135} onChange={v => set({ gradAngle: v })} min={0} max={360} />
        {groupTitle('متن و چرخش')}
        <FSelect label="سایه متن" value={s.textShadow} onChange={v => set({ textShadow: v })} options={TEXT_SHADOW_OPTS} />
        <FNumber label="فاصله حروف (px)" value={s.letterSpacing} onChange={v => set({ letterSpacing: v })} min={0} max={20} />
        <FNumber label="چرخش (درجه)" value={s.rotate} onChange={v => set({ rotate: v })} min={-180} max={180} />

        {groupTitle('ریزه‌کاری‌های داخلی — تیتر')}
        <FNumber label="اندازه تیتر (px)" value={s.parts.title.size} onChange={v => setPart('title', { size: v })} min={10} max={64} />
        <FSelect label="ضخامت تیتر" value={s.parts.title.weight} onChange={v => setPart('title', { weight: v })}
          options={[{ value: '', label: 'پیش‌فرض' }, { value: '500', label: 'معمولی' }, { value: '700', label: 'ضخیم' }, { value: '800', label: 'خیلی ضخیم' }, { value: '900', label: 'سیاه' }]} />
        <FColor label="رنگ تیتر" value={s.parts.title.color} onChange={v => setPart('title', { color: v })} />
        <FSelect label="تراز تیتر" value={s.parts.title.align} onChange={v => setPart('title', { align: v })}
          options={[{ value: '', label: 'پیش‌فرض' }, { value: 'right', label: 'راست' }, { value: 'center', label: 'وسط' }, { value: 'left', label: 'چپ' }]} />

        {groupTitle('ریزه‌کاری‌های داخلی — توضیح')}
        <FNumber label="اندازه توضیح (px)" value={s.parts.desc.size} onChange={v => setPart('desc', { size: v })} min={9} max={32} />
        <FColor label="رنگ توضیح" value={s.parts.desc.color} onChange={v => setPart('desc', { color: v })} />
        <FNumber label="فاصله خطوط توضیح" value={s.parts.desc.lineHeight} onChange={v => setPart('desc', { lineHeight: v })} min={1} max={3} />

        {groupTitle('ریزه‌کاری‌های داخلی — دکمه')}
        <FSelect label="مدل دکمه" value={s.parts.button.style} onChange={v => setPart('button', { style: v })}
          options={[{ value: '', label: 'پیش‌فرض' }, { value: 'solid', label: 'پُر' }, { value: 'outline', label: 'کادردار' }, { value: 'soft', label: 'ملایم' }]} />
        <FNumber label="گردی دکمه (px)" value={s.parts.button.radius} onChange={v => setPart('button', { radius: v })} min={0} max={120} />
        <FNumber label="اندازه متن دکمه (px)" value={s.parts.button.size} onChange={v => setPart('button', { size: v })} min={9} max={28} />
        <FSelect label="ضخامت متن دکمه" value={s.parts.button.weight} onChange={v => setPart('button', { weight: v })}
          options={[{ value: '', label: 'پیش‌فرض' }, { value: '500', label: 'معمولی' }, { value: '700', label: 'ضخیم' }, { value: '800', label: 'خیلی ضخیم' }]} />
        <FColor label="رنگ دکمه" value={s.parts.button.bg} onChange={v => setPart('button', { bg: v })} />
        <FColor label="رنگ متن دکمه" value={s.parts.button.color} onChange={v => setPart('button', { color: v })} />
        <FNumber label="فاصله عمودی دکمه (px)" value={s.parts.button.padY} onChange={v => setPart('button', { padY: v })} min={0} max={40} />
        <FNumber label="فاصله افقی دکمه (px)" value={s.parts.button.padX} onChange={v => setPart('button', { padX: v })} min={0} max={80} />

        {groupTitle('ریزه‌کاری‌های داخلی — آیکون و بج')}
        <FNumber label="اندازه آیکون (px)" value={s.parts.icon.size} onChange={v => setPart('icon', { size: v })} min={12} max={120} />
        <FNumber label="گردی پس‌زمینه آیکون (px)" value={s.parts.icon.radius} onChange={v => setPart('icon', { radius: v })} min={0} max={60} />
        <FColor label="رنگ پس‌زمینه آیکون" value={s.parts.icon.bg} onChange={v => setPart('icon', { bg: v })} />
        <FColor label="رنگ آیکون" value={s.parts.icon.color} onChange={v => setPart('icon', { color: v })} />
        <FNumber label="گردی بج/چیپ (px)" value={s.parts.chip.radius} onChange={v => setPart('chip', { radius: v })} min={0} max={60} />
        <FColor label="رنگ بج/چیپ" value={s.parts.chip.bg} onChange={v => setPart('chip', { bg: v })} />
        <FColor label="رنگ متن بج/چیپ" value={s.parts.chip.color} onChange={v => setPart('chip', { color: v })} />
        <FNumber label="فاصله داخلی کارت‌ها (px)" value={s.parts.card.gap} onChange={v => setPart('card', { gap: v })} min={0} max={48} />
      </div>
    </Section>
  );
}

/* ---------- shell: wraps any widget editor into the two separated groups ---------- */
export function WidgetShell({ settings, onChange, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <WidgetContentSection>{children}</WidgetContentSection>
      <WidgetStyleSection value={settings.style} onChange={v => onChange({ ...settings, style: v })} />
    </div>
  );
}
